/**
 * The organisation: its details, its people, and what its services point at.
 *
 * A super admin picks which organisation; everyone else sees their own and
 * cannot change the list.
 *
 * The service credentials are split across two stores and it matters. `hlr`
 * and `apns` are read by the app server and go through its own settings
 * endpoints; `clickhouse`, `security` and `worker_url` are read by the gateway
 * and go through that. Writing to the wrong one saves happily and changes
 * nothing, which is what the old settings screen did with the phone-checking
 * credentials. See the table in `api/gateway.ts`.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, Loader, Modal, PasswordInput, Select, Stack, Switch,
  Table, Tabs, Text, Textarea, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAlertTriangle } from '@tabler/icons-react';
import * as gw from '../../api/gateway';
import { useAuth } from '../../auth/AuthProvider';
import {
  useApnsSettings, useEnums, useHlrSettings, useSaveApnsSettings, useSaveHlrSettings,
  useAiSettings, useSaveAiSettings, useSaveSesSettings, useSesHook, useSesSettings,
} from '../../api/queries';
import { TOKENS, formatDateTime } from '../../theme';
import { PermissionsTab } from './PermissionsTab';
import { EmailVerificationCard } from './EmailVerificationCard';
import { roleLabel, useVocab } from '../../vocab';

/**
 * The roles to offer, from the server rather than from a copy kept here.
 *
 * A list written out in this file misses one the day another is added, and a
 * role the list does not know renders as a blank dropdown beside a real
 * person. `supplier` was in use and missing from the first version of this.
 *
 * `current` is always included even if the server does not list it, so an
 * unfamiliar role is shown rather than silently blanked.
 */
function useRoles(current?: string): string[] {
  const enums = useEnums();
  const known = enums.data?.roles ?? [];
  return current && !known.includes(current) ? [...known, current] : [...known];
}

export function OrganisationTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSuperAdmin = user?.role === 'super_admin';

  const orgs = useQuery({ queryKey: ['tenants'], queryFn: gw.tenants, enabled: isSuperAdmin });
  const [orgId, setOrgId] = useState<string | null>(user?.tenant_id ?? null);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (!orgId && user?.tenant_id) setOrgId(user.tenant_id);
  }, [user?.tenant_id, orgId]);

  const detail = useQuery({
    queryKey: ['tenant', orgId],
    queryFn: () => gw.tenant(orgId!),
    enabled: !!orgId,
  });
  const members = useQuery({
    queryKey: ['tenant-users', orgId],
    queryFn: () => gw.tenantUsers(orgId!),
    enabled: !!orgId,
  });
  const security = useQuery({
    queryKey: ['org-security', orgId],
    queryFn: () => gw.orgSecurity(orgId!),
    enabled: !!orgId,
  });

  /* The organisation whose settings are on screen is only ever this user's own
     unless they are a super admin, so the phone-checking endpoints, which act
     on the caller's own tenant, are correct for that case and hidden for the
     rest. */
  const ownOrg = orgId === user?.tenant_id;

  return (
    <Stack>
      {isSuperAdmin && (
        <Group align="flex-end">
          <Select
            label="Organisation"
            data={(orgs.data?.tenants ?? []).map((t) => ({ value: t.id, label: `${t.name} (${t.plan})` }))}
            value={orgId}
            onChange={setOrgId}
            searchable
            allowDeselect={false}
            w={340}
          />
          <Button variant="light" onClick={() => setCreating(true)}>New organisation</Button>
        </Group>
      )}

      <NewOrganisation
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['tenants'] });
          setOrgId(id);
        }}
      />

      <Tabs defaultValue="details">
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="people">Members</Tabs.Tab>
          <Tabs.Tab value="permissions">Permissions</Tabs.Tab>
          <Tabs.Tab value="services">Services</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details" pt="md">
          <Stack>
            {/* The Wording and Features cards are gone. Every organisation
                reads the same words, and money shows where a stream sells
                while the rest follows each login's tabs (both settings are
                still stored, for installed apps). */}
            <OrgDetails detail={detail} security={security} orgId={orgId} qc={qc} />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="people" pt="md">
          <People members={members} orgId={orgId} qc={qc} isSuperAdmin={isSuperAdmin} />
        </Tabs.Panel>

        <Tabs.Panel value="permissions" pt="md">
          <PermissionsTab orgId={orgId} />
        </Tabs.Panel>

        <Tabs.Panel value="services" pt="md">
          <Services detail={detail} orgId={orgId} ownOrg={ownOrg} qc={qc} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/**
 * The states an account can be in, from the server for the same reason as the
 * roles. Anything that is not `active` bars the account; the difference
 * between suspended and inactive is what it tells whoever reads it later.
 */
function StatusSelect({ value, onChange }: { value: string; onChange: (s: string) => void }) {
  const enums = useEnums();
  const known = enums.data?.account_statuses ?? [];
  const offered = value && !known.includes(value) ? [...known, value] : [...known];
  return (
    <Select
      size="xs"
      data={offered}
      value={value}
      onChange={(v) => v && onChange(v)}
      allowDeselect={false}
    />
  );
}

/**
 * Starting a new organisation.
 *
 * It arrives empty: no people, no server address, no credentials. The first
 * thing anyone will want is a person in it, so the dialog says so rather than
 * leaving somebody hunting for why nobody can sign in.
 */
function NewOrganisation({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [plan, setPlan] = useState('starter');
  /* Typed once from the name, then left alone: a reference that keeps
     rewriting itself while being edited is impossible to correct. */
  const [slugEdited, setSlugEdited] = useState(false);

  const create = useMutation({
    mutationFn: () => gw.createTenant({ name: name.trim(), slug: slug.trim(), plan }),
    onSuccess: (r) => {
      onCreated(r.tenant.id);
      onClose();
      setName('');
      setSlug('');
      setSlugEdited(false);
      notifications.show({
        color: 'teal',
        title: 'Organisation created',
        message: 'Add somebody to it under People, and set its server address under Services.',
      });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not create it', message: (e as Error).message }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title="New organisation" size="md">
      <Stack>
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setName(v);
            if (!slugEdited) setSlug(v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
          }}
        />
        <TextInput
          label="Reference"
          description="Short, lowercase, used in addresses."
          value={slug}
          onChange={(e) => {
            setSlugEdited(true);
            setSlug(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
          }}
        />
        <Select
          label="Plan"
          data={['starter', 'pro', 'enterprise']}
          value={plan}
          onChange={(v) => setPlan(v ?? 'starter')}
          allowDeselect={false}
        />
        <Alert color="gray" variant="light">
          <Text size="sm">
            It starts empty. Nobody can sign in to it until you add a person under People, and
            nothing will work until its server address is set under Services.
          </Text>
        </Alert>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button
            loading={create.isPending}
            disabled={!name.trim() || !slug.trim()}
            onClick={() => create.mutate()}
          >
            Create it
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** One place that knows how a role is picked. */
function RoleSelect({
  value,
  isSuperAdmin,
  onChange,
  label,
}: {
  value: string;
  isSuperAdmin: boolean;
  onChange: (role: string) => void;
  label?: string;
}) {
  const v = useVocab();
  const roles = useRoles(value);
  // Only a super admin can hand out their own level of access.
  const offered = isSuperAdmin ? roles : roles.filter((r) => r !== 'super_admin');
  return (
    <Select
      size={label ? undefined : 'xs'}
      label={label}
      // The key is what is stored; only the words beside it are for show.
      data={offered.map((r) => ({ value: r, label: roleLabel(v, r) }))}
      value={value}
      onChange={(v) => v && onChange(v)}
      allowDeselect={false}
    />
  );
}

/* ---------- details ---------- */

function OrgDetails({
  detail,
  security,
  orgId,
  qc,
}: {
  detail: ReturnType<typeof useQuery<gw.TenantDetail>>;
  security: ReturnType<typeof useQuery<gw.OrgSecurity>>;
  orgId: string | null;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const t = detail.data?.tenant;
  const [form, setForm] = useState({ name: '', slug: '', plan: '', status: '' });
  useEffect(() => {
    setForm({
      name: t?.name ?? '',
      slug: t?.slug ?? '',
      plan: t?.plan ?? '',
      status: t?.status ?? '',
    });
  }, [t?.name, t?.slug, t?.plan, t?.status]);

  const changed =
    t != null &&
    (form.name !== t.name || form.slug !== t.slug || form.plan !== t.plan || form.status !== t.status);

  const save = useMutation({
    mutationFn: () =>
      gw.updateTenant(orgId!, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        plan: form.plan,
        status: form.status,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', orgId] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      notifications.show({ color: 'teal', title: 'Saved', message: 'The organisation has been updated.' });
    },
    onError: (e) => notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  const requireTotp = useMutation({
    mutationFn: (on: boolean) => gw.setOrgSecurity(orgId!, on),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-security', orgId] });
      notifications.show({ color: 'teal', title: 'Saved', message: 'Sign-in requirements updated.' });
    },
    onError: (e) => notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  return (
    <Stack>
      <Card withBorder radius="md" padding="lg">
        <Stack>
          <Text fw={600}>Organisation</Text>
          <Group grow>
            <TextInput
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            />
            <TextInput
              label="Reference"
              description="Used in addresses. Changing it can break saved links."
              value={form.slug}
              onChange={(e) =>
                setForm({ ...form, slug: e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })
              }
            />
          </Group>
          <Group grow>
            <Select
              label="Plan"
              data={['starter', 'pro', 'enterprise']}
              value={form.plan}
              onChange={(v) => v && setForm({ ...form, plan: v })}
              allowDeselect={false}
            />
            <Select
              label="Status"
              description="Anything but active stops everyone here signing in."
              data={['active', 'suspended', 'inactive']}
              value={form.status}
              onChange={(v) => v && setForm({ ...form, status: v })}
              allowDeselect={false}
            />
          </Group>
          {form.status !== 'active' && t?.status === 'active' && (
            <Alert color="red" variant="light">
              <Text size="sm">
                Saving this signs out everyone in {t?.name} and stops them getting back in.
              </Text>
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              loading={save.isPending}
              disabled={!changed || !form.name.trim() || !form.slug.trim()}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Stack>
          <Text fw={600}>Signing in</Text>
          {/* Disabled until the answer arrives. Rendered from `?? false` the
              switch sits at off whatever the truth is, and a click on it does
              not toggle the setting, it writes the guess. */}
          <Switch
            checked={security.data?.enforce_2fa ?? false}
            disabled={security.isPending || requireTotp.isPending}
            onChange={(e) => requireTotp.mutate(e.currentTarget.checked)}
            label="Require two-factor authentication"
            description="When on, everyone in this organisation has to set it up at their next sign in. When off they decide for themselves, and anyone already enrolled still enters a code."
          />
        </Stack>
      </Card>
    </Stack>
  );
}

/* ---------- people ---------- */

function People({
  members,
  orgId,
  qc,
  isSuperAdmin,
}: {
  members: ReturnType<typeof useQuery<{ users: gw.GatewayAccount[] }>>;
  orgId: string | null;
  qc: ReturnType<typeof useQueryClient>;
  isSuperAdmin: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', role: 'viewer', password: '' });

  const refresh = () => qc.invalidateQueries({ queryKey: ['tenant-users', orgId] });
  const shout = (e: unknown) =>
    notifications.show({ color: 'red', title: 'That did not work', message: (e as Error).message });

  const add = useMutation({
    mutationFn: () => gw.createUser(orgId!, form),
    onSuccess: () => {
      setAdding(false);
      setForm({ email: '', first_name: '', last_name: '', role: 'viewer', password: '' });
      refresh();
      notifications.show({ color: 'teal', title: 'Added', message: 'They can sign in now.' });
    },
    onError: shout,
  });
  const role = useMutation({
    mutationFn: (v: { id: string; role: string }) => gw.setUserRole(v.id, v.role),
    onSuccess: refresh,
    onError: shout,
  });
  const status = useMutation({
    mutationFn: (v: { id: string; status: string }) => gw.setUserStatus(v.id, v.status),
    onSuccess: refresh,
    onError: shout,
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={600}>People in this organisation</Text>
        <Button size="compact-sm" onClick={() => setAdding(true)}>Add someone</Button>
      </Group>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Last signed in</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {members.isPending && (
            <Table.Tr><Table.Td colSpan={5}>
              <Group justify="center" py="lg"><Loader size="sm" /></Group>
            </Table.Td></Table.Tr>
          )}
          {!members.isPending && (members.data?.users ?? []).length === 0 && (
            <Table.Tr><Table.Td colSpan={5}>
              <Text ta="center" c="dimmed" py="lg">Nobody here yet.</Text>
            </Table.Td></Table.Tr>
          )}
          {(members.data?.users ?? []).map((m) => (
            <Table.Tr key={m.id}>
              <Table.Td>
                <Text size="sm">{[m.first_name, m.last_name].filter(Boolean).join(' ') || '-'}</Text>
              </Table.Td>
              <Table.Td><Text size="sm">{m.email}</Text></Table.Td>
              <Table.Td w={160}>
                <RoleSelect
                  value={m.role}
                  isSuperAdmin={isSuperAdmin}
                  onChange={(v) => role.mutate({ id: m.id, role: v })}
                />
              </Table.Td>
              <Table.Td w={150}>
                <StatusSelect
                  value={m.status}
                  onChange={(v) => status.mutate({ id: m.id, status: v })}
                />
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {m.last_login_at ? formatDateTime(m.last_login_at) : 'Never'}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={adding} onClose={() => setAdding(false)} title="Add someone" size="md">
        <Stack>
          <TextInput
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
          />
          <Group grow>
            <TextInput
              label="First name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.currentTarget.value })}
            />
            <TextInput
              label="Last name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.currentTarget.value })}
            />
          </Group>
          <RoleSelect
            label="Role"
            value={form.role}
            isSuperAdmin={isSuperAdmin}
            onChange={(v) => setForm({ ...form, role: v })}
          />
          <PasswordInput
            label="Starting password"
            description="They should change it once they are in."
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.currentTarget.value })}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              loading={add.isPending}
              disabled={!form.email.includes('@') || form.password.length < 8}
              onClick={() => add.mutate()}
            >
              Add them
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/* ---------- services ---------- */

function Services({
  detail,
  orgId,
  ownOrg,
  qc,
}: {
  detail: ReturnType<typeof useQuery<gw.TenantDetail>>;
  orgId: string | null;
  ownOrg: boolean;
  qc: ReturnType<typeof useQueryClient>;
}) {
  return (
    <Stack>
      {ownOrg ? <PhoneChecking /> : (
        <Alert color="gray" variant="light">
          <Text size="sm">
            Phone-checking credentials are stored on each organisation&apos;s own server, so they
            can only be changed while signed in to that organisation.
          </Text>
        </Alert>
      )}
      {ownOrg && <EmailSending />}
      {ownOrg && <EmailVerificationCard />}
      {ownOrg && <AiEditing />}
      {ownOrg && <PushNotifications />}
      <ClickHouseCard detail={detail} orgId={orgId} qc={qc} />
      <WorkerUrlCard detail={detail} orgId={orgId} qc={qc} />
    </Stack>
  );
}

/**
 * Amazon SES, which sends the emails designed under Emails.
 *
 * Unlike the number-checking keys, the secret is never sent back: the box
 * starts empty and is only written when something is typed into it.
 */
function EmailSending() {
  const settings = useSesSettings(true);
  const save = useSaveSesSettings();
  const [form, setForm] = useState({
    region: '', access_key_id: '', secret_access_key: '', configuration_set: '',
    company_name: '', company_address: '',
  });

  useEffect(() => {
    if (!settings.data) return;
    const d = settings.data;
    setForm({
      region: d.region, access_key_id: d.access_key_id, secret_access_key: '',
      configuration_set: d.configuration_set, company_name: d.company_name, company_address: d.company_address,
    });
  }, [settings.data]);

  const current = settings.data;
  // Read the value now: by the time React runs the updater, the event's
  // currentTarget has been cleared, and reading it then crashed the page.
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack>
        <Group gap="xs">
          <Text fw={600}>Email sending (Amazon SES)</Text>
          <Badge color={current?.configured ? 'teal' : 'chartAmber'} variant="light">
            {current?.configured ? 'Configured' : 'Not set up'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Sends the emails designed under Emails. Use an access key that can only send email.
        </Text>
        <Group grow>
          <TextInput label="Region" placeholder="eu-north-1" value={form.region} onChange={set('region')} />
          <TextInput label="Configuration set" description="Optional. Reports bounces, opens and clicks."
            value={form.configuration_set} onChange={set('configuration_set')} />
        </Group>
        <TextInput label="Access key ID" value={form.access_key_id} onChange={set('access_key_id')} />
        <PasswordInput
          label="Secret access key"
          placeholder={current?.secret_set ? 'Saved. Type a new one to replace it.' : ''}
          value={form.secret_access_key}
          onChange={set('secret_access_key')}
        />
        <Group grow>
          <TextInput label="Company name" description="Shown at the foot of every email"
            placeholder="Tomorrow Media Group Ltd" value={form.company_name} onChange={set('company_name')} />
          <TextInput label="Company address" description="Required on marketing email"
            value={form.company_address} onChange={set('company_address')} />
        </Group>
        {current?.configured && <SesEventFeed />}
        <Group justify="flex-end">
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(form, {
                onSuccess: () => {
                  setForm((f) => ({ ...f, secret_access_key: '' }));
                  notifications.show({ color: 'teal', title: 'Saved', message: 'Email sending updated.' });
                },
                onError: (e) =>
                  notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
              })
            }
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Anthropic, for "Ask AI" in the email editor. The key is never sent back:
 * the box starts empty and is only written when something is typed into it.
 */
function AiEditing() {
  const vocab = useVocab();
  const settings = useAiSettings(true);
  const save = useSaveAiSettings();
  const [key, setKey] = useState('');
  const [model, setModel] = useState('');
  const [banned, setBanned] = useState('');
  useEffect(() => {
    if (!settings.data) return;
    setModel(settings.data.model);
    setBanned(settings.data.banned_terms.join('\n'));
  }, [settings.data]);
  const bannedList = () => banned.split('\n').map((t) => t.trim()).filter(Boolean);
  const current = settings.data;

  function submit(
    body: { api_key: string; model: string; remove_key?: boolean; assistant_enabled?: boolean; banned_terms?: string[] },
    done: string,
  ) {
    save.mutate(body, {
      onSuccess: () => {
        setKey('');
        notifications.show({ color: 'teal', title: 'Saved', message: done });
      },
      onError: (e) => notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
    });
  }

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack>
        <Group gap="xs">
          <Text fw={600}>AI (Anthropic)</Text>
          <Badge color={current?.key_set ? 'teal' : 'chartAmber'} variant="light">
            {current?.key_set ? 'Configured' : 'Not set up'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Powers Ask AI in the email editor and the assistant. Make a key at console.anthropic.com
          under API keys.
        </Text>
        <PasswordInput
          label="API key"
          placeholder={current?.key_set ? 'Saved. Type a new one to replace it.' : 'sk-ant-...'}
          value={key}
          onChange={(e) => { const v = e.currentTarget.value; setKey(v); }}
        />
        <TextInput
          label="Model"
          description={`Optional. Leave empty for ${current?.default_model ?? 'the default'}.`}
          placeholder={current?.default_model}
          value={model}
          onChange={(e) => { const v = e.currentTarget.value; setModel(v); }}
        />
        <Switch
          label="Assistant"
          description={`Lets admins ask questions about ${vocab.many('campaign')}, routing, ${vocab.many('lead')}, reports and emails. It can only read. Answers can include ${vocab.one('lead')} details, which are sent to Anthropic to answer.`}
          checked={!!current?.assistant_enabled}
          disabled={!current?.key_set || save.isPending}
          onChange={(e) => {
            const on = e.currentTarget.checked;
            submit({ api_key: '', model, assistant_enabled: on }, on ? 'The assistant is on.' : 'The assistant is off.');
          }}
        />
        <Textarea
          label="Words the AI must never say"
          description={`One per line, such as names, ${vocab.many('supplier')} or projects. Replaced with [removed] in anything the AI writes. ${current?.built_in_banned ?? 'Several'} technical terms about how Rowstorm is built are always blocked as well.`}
          autosize
          minRows={3}
          maxRows={10}
          value={banned}
          onChange={(e) => { const v = e.currentTarget.value; setBanned(v); }}
        />
        <Group justify="flex-end">
          {current?.key_set && (
            <Button variant="subtle" color="red" disabled={save.isPending}
              onClick={() => submit({ api_key: '', model, remove_key: true }, 'The API key was removed.')}>
              Remove key
            </Button>
          )}
          <Button loading={save.isPending} onClick={() => submit({ api_key: key, model, banned_terms: bannedList() }, 'AI settings updated.')}>
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * The address Amazon SNS posts SES events to. Bounces and complaints put
 * addresses on the never-email list; opens and clicks fill the Emails
 * overview. Without it only sends and unsubscribes are counted.
 */
function SesEventFeed() {
  const hook = useSesHook(true);
  return (
    <Stack gap={6} p="sm" style={{ background: TOKENS.surface2, borderRadius: 8 }}>
      <Text size="sm" fw={600}>Bounces, complaints, opens and clicks</Text>
      {/* eslint-disable-next-line no-restricted-syntax -- "event destination" is AWS SES's own term */}
      <Text size="xs" c="dimmed">
        In Amazon SES, make a configuration set with an event destination of type SNS (events: sends,
        deliveries, bounces, complaints, rejects, opens, clicks). Subscribe this address to its SNS topic
        over HTTPS, then put the configuration set&apos;s name in the box above.
      </Text>
      <TextInput
        readOnly
        value={hook.data?.url ?? ''}
        placeholder={hook.isPending ? 'Loading' : ''}
        onFocus={(e) => e.currentTarget.select()}
        aria-label="SNS subscription address"
      />
    </Stack>
  );
}

/** Read by the app server, so it goes through the app server. */
function PhoneChecking() {
  const settings = useHlrSettings(true);
  const save = useSaveHlrSettings();
  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');
  const [testMode, setTestMode] = useState(false);

  /*
    The stored key and secret are write-only: the server says whether each is
    set (and the key's last four characters), never the values. The boxes
    start empty, and an empty box keeps what is stored.
  */
  useEffect(() => {
    if (!settings.data) return;
    setKey('');
    setSecret('');
    setTestMode(settings.data.test_mode);
  }, [settings.data]);

  const current = settings.data;
  const changed =
    current != null && (key !== '' || secret !== '' || testMode !== current.test_mode);
  const keyHint = current?.has_api_key
    ? current.api_key_last4
      ? `A key ending ${current.api_key_last4} is stored. Leave empty to keep it.`
      : 'A key is stored. Leave empty to keep it.'
    : undefined;
  const secretHint = current?.has_api_secret
    ? 'A secret is stored. Leave empty to keep it.'
    : undefined;

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack>
        <Group gap="xs">
          <Text fw={600}>Number checking</Text>
          <Badge color={current?.configured ? 'teal' : 'chartAmber'} variant="light">
            {current?.configured ? 'Configured' : 'Not set up'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Used when you check a column of phone numbers. Stored credentials are never shown;
          type a new one to replace it.
        </Text>
        <PasswordInput
          label="API key"
          description={keyHint}
          value={key}
          onChange={(e) => setKey(e.currentTarget.value)}
        />
        <PasswordInput
          label="API secret"
          description={secretHint}
          value={secret}
          onChange={(e) => setSecret(e.currentTarget.value)}
        />
        <Switch
          checked={testMode}
          onChange={(e) => setTestMode(e.currentTarget.checked)}
          label="Use the free testing endpoint"
          description="Checks cost nothing and return invented answers."
        />
        {testMode && (
          <Alert color="chartAmber" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              While this is on, every number comes back with a made-up result, including numbers
              checked during a live import, and nothing afterwards says so.
            </Text>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            loading={save.isPending}
            disabled={!changed}
            onClick={() =>
              save.mutate(
                { api_key: key, api_secret: secret, test_mode: testMode },
                {
                  onSuccess: () =>
                    notifications.show({ color: 'teal', title: 'Saved', message: 'Number checking updated.' }),
                  onError: (e) =>
                    notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
                },
              )
            }
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** Also read by the app server, so it goes there too. */
function PushNotifications() {
  const settings = useApnsSettings(true);
  const save = useSaveApnsSettings();
  const [form, setForm] = useState({ key_id: '', team_id: '', p8_key: '', bundle_id: '' });
  // The private key is write-only: the box starts empty, and an empty box
  // keeps the stored key.
  useEffect(() => {
    if (!settings.data) return;
    const { key_id, team_id, bundle_id } = settings.data;
    setForm({ key_id, team_id, p8_key: '', bundle_id });
  }, [settings.data]);

  const current = settings.data;
  const changed =
    current != null &&
    (form.key_id !== current.key_id ||
      form.team_id !== current.team_id ||
      form.p8_key !== '' ||
      form.bundle_id !== current.bundle_id);

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack>
        <Group gap="xs">
          <Text fw={600}>Push notifications</Text>
          <Badge color={current?.configured ? 'teal' : 'chartAmber'} variant="light">
            {current?.configured ? 'Configured' : 'Not set up'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Apple&apos;s credentials for sending alerts to the phone app. The private key signs
          pushes as your app, so treat it like a password.
        </Text>
        <Group grow>
          <TextInput
            label="Key ID"
            value={form.key_id}
            onChange={(e) => setForm({ ...form, key_id: e.currentTarget.value })}
          />
          <TextInput
            label="Team ID"
            value={form.team_id}
            onChange={(e) => setForm({ ...form, team_id: e.currentTarget.value })}
          />
        </Group>
        <TextInput
          label="Bundle ID"
          placeholder="com.example.app"
          value={form.bundle_id}
          onChange={(e) => setForm({ ...form, bundle_id: e.currentTarget.value })}
        />
        <Textarea
          label="Private key (.p8)"
          description={
            current?.has_p8_key
              ? 'A key is stored and never shown. Paste the whole new file to replace it, or leave empty to keep it.'
              : 'The whole file, including the BEGIN and END lines.'
          }
          autosize
          minRows={4}
          maxRows={10}
          value={form.p8_key}
          onChange={(e) => setForm({ ...form, p8_key: e.currentTarget.value })}
        />
        <Group justify="flex-end">
          <Button
            loading={save.isPending}
            disabled={!changed}
            onClick={() =>
              save.mutate(form, {
                onSuccess: () =>
                  notifications.show({ color: 'teal', title: 'Saved', message: 'Push credentials updated.' }),
                onError: (e) =>
                  notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
              })
            }
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** Read by the gateway, so it goes through the gateway. */
function ClickHouseCard({
  detail,
  orgId,
  qc,
}: {
  detail: ReturnType<typeof useQuery<gw.TenantDetail>>;
  orgId: string | null;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const v = useVocab();
  const cfg = (detail.data?.configs?.clickhouse ?? {}) as Record<string, string>;
  const [host, setHost] = useState('');
  const [database, setDatabase] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  useEffect(() => {
    setHost(cfg.host ?? '');
    setDatabase(cfg.database ?? '');
    setUsername(cfg.username ?? '');
  }, [cfg.host, cfg.database, cfg.username]);

  const save = useMutation({
    mutationFn: () =>
      gw.setTenantConfig(orgId!, 'clickhouse', {
        ...cfg,
        host: host.trim(),
        database: database.trim(),
        username: username.trim(),
        ...(password ? { password } : {}),
      }),
    onSuccess: () => {
      setPassword('');
      qc.invalidateQueries({ queryKey: ['tenant', orgId] });
      notifications.show({ color: 'teal', title: 'Saved', message: 'Database settings updated.' });
    },
    onError: (e) => notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  const test = useMutation({
    mutationFn: () => gw.testClickHouse(orgId!),
    onSuccess: (r) =>
      notifications.show({
        color: 'teal',
        title: 'Connected',
        message: r.message ?? 'The database answered.',
      }),
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not connect', message: (e as Error).message }),
  });

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack>
        <Text fw={600}>{`Database for ${v.many('lead')}`}</Text>
        <Text size="sm" c="dimmed">
          {`Where this organisation's ${v.many('lead')} are stored. The password is write-only: leave it
          empty to keep the one already saved.`}
        </Text>
        <Group grow>
          <TextInput label="Host" value={host} onChange={(e) => setHost(e.currentTarget.value)} />
          <TextInput label="Database" value={database} onChange={(e) => setDatabase(e.currentTarget.value)} />
        </Group>
        <Group grow>
          <TextInput label="Username" value={username} onChange={(e) => setUsername(e.currentTarget.value)} />
          <PasswordInput
            label="Password"
            placeholder="Saved. Type to replace it."
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
        </Group>
        <Group justify="flex-end">
          <Button
            variant="light"
            loading={test.isPending}
            disabled={detail.isPending}
            onClick={() => test.mutate()}
          >
            Test the connection
          </Button>
          {/* Every field here starts empty and is filled in once the stored
              config arrives, so a save before then writes those blanks over a
              working connection. */}
          <Button loading={save.isPending} disabled={detail.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** Read by the gateway, which uses it to send people to the right server. */
function WorkerUrlCard({
  detail,
  orgId,
  qc,
}: {
  detail: ReturnType<typeof useQuery<gw.TenantDetail>>;
  orgId: string | null;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const stored = (detail.data?.configs?.worker_url ?? {}) as Record<string, string> | string;
  const initial = typeof stored === 'string' ? stored : (stored.url ?? stored.value ?? '');
  const [url, setUrl] = useState('');
  useEffect(() => setUrl(initial), [initial]);

  const save = useMutation({
    mutationFn: () => gw.setTenantConfig(orgId!, 'worker_url', url.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', orgId] });
      notifications.show({ color: 'teal', title: 'Saved', message: 'Server address updated.' });
    },
    onError: (e) => notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack>
        <Text fw={600}>Server address</Text>
        <Text size="sm" c="dimmed">
          Where this organisation&apos;s app server lives. Everyone signing in is sent here, so a
          wrong value locks the organisation out until it is corrected.
        </Text>
        <TextInput
          placeholder="https://api.example.com"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button loading={save.isPending} disabled={url === initial} onClick={() => save.mutate()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
